// ChatTest.jsx — Prototype chat IA concierge — données Firestore complètes (lecture seule)
// Accès local : http://localhost:3000/chat-test
// Prérequis : REACT_APP_ANTHROPIC_API_KEY dans .env.local

import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, RefreshCw } from 'lucide-react';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { loadInitialData } from '../utils/dataLoaders';

// ─────────────────────────────────────────────────────────────────────────────
// INFOS STATIQUES HÔTEL
// ─────────────────────────────────────────────────────────────────────────────

const HOTEL = {
  name: 'Maison Ceronne',
  location: 'Normandie (Soligny-la-Trappe, 61)',
  checkIn: '16h00',
  checkOut: '11h00',
  contact: 'contact@maisonceronne.com',
};

const RESTAURANT = {
  service: 'dîner (19h30–21h30) et déjeuner weekend (12h–13h30)',
  closed: 'lundi et mardi soir',
  reservationNote: 'Réservation en ligne sur maisonceronne.com/restauration ou par téléphone',
  menu: 'Cuisine du terroir normand, menu du marché (prix sur le site)',
};

const SERVICES = [
  'Petit-déjeuner inclus ou en option selon la formule',
  'Spa & balnéo (sur réservation)',
  'Location de vélos',
  'Transfert gare (sur demande)',
];

// ─────────────────────────────────────────────────────────────────────────────
// UTILITAIRES DATES
// ─────────────────────────────────────────────────────────────────────────────

function nightsBetween(d1, d2) {
  return Math.round((new Date(d2) - new Date(d1)) / 86400000);
}

function toDateStr(d) {
  if (!d) return '';
  const dt = d instanceof Date ? d : (d.toDate ? d.toDate() : new Date(d));
  return dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function isoToDate(iso) {
  return new Date(iso + 'T12:00:00');
}

function dateRangeNights(checkIn, checkOut) {
  const nights = [];
  const cur = new Date(isoToDate(checkIn));
  const end = new Date(isoToDate(checkOut));
  while (cur < end) {
    nights.push(cur.toISOString().split('T')[0]);
    cur.setDate(cur.getDate() + 1);
  }
  return nights;
}

// ─────────────────────────────────────────────────────────────────────────────
// LOGIQUE CONTRAINTES
// ─────────────────────────────────────────────────────────────────────────────

// Trouver la règle de priorité la plus haute applicable à une période + type
function getApplicableRule(checkIn, checkOut, roomTypeId, availabilityRules) {
  const ci = isoToDate(checkIn);
  const co = isoToDate(checkOut);
  return availabilityRules.find(rule => {
    const overlaps = ci < rule.endDate && co > rule.startDate;
    const appliesToType = rule.roomTypes.length === 0 || rule.roomTypes.includes(roomTypeId);
    return overlaps && appliesToType;
  }) || null; // déjà trié priority desc par Firestore
}

// Vérifier si des nuits sont bloquées pour un type de chambre
function getBlockedNightsInPeriod(checkIn, checkOut, roomTypeId, blockedDates) {
  const nights = dateRangeNights(checkIn, checkOut);
  return blockedDates.filter(bd => {
    const bdStr = bd.date instanceof Date
      ? bd.date.toISOString().split('T')[0]
      : bd.date.toDate?.()?.toISOString().split('T')[0];
    return nights.includes(bdStr) && (bd.roomTypes.length === 0 || bd.roomTypes.includes(roomTypeId));
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// LOGIQUE DE DISPONIBILITÉ
// ─────────────────────────────────────────────────────────────────────────────

function isRoomAvailableForPeriod(roomId, checkIn, checkOut, reservations, roomBlocks) {
  const ci = isoToDate(checkIn);
  const co = isoToDate(checkOut);

  // Vérifier les réservations
  const hasResConflict = reservations.some(res => {
    if (res.status === 'cancelled') return false;
    const rci = res._checkInNorm;
    const rco = res._checkOutNorm;
    if (ci < rco && co > rci) {
      return res.rooms?.some(rr => rr.roomId === roomId) || res.roomId === roomId;
    }
    return false;
  });
  if (hasResConflict) return false;

  // Vérifier les blocages manuels (room_blocks)
  const hasBlockConflict = roomBlocks.some(block => {
    if (block.roomId !== roomId) return false;
    const bStart = block.startDate?.toDate ? block.startDate.toDate() : new Date(block.startDate);
    const bEnd = block.endDate?.toDate ? block.endDate.toDate() : new Date(block.endDate);
    return ci < bEnd && co > bStart;
  });

  return !hasBlockConflict;
}

function isRoomTypeAvailableForPeriod(rt, checkIn, checkOut, rooms, reservations, roomBlocks, blockedDates) {
  // Vérifier les dates bloquées globalement pour ce type
  const blocked = getBlockedNightsInPeriod(checkIn, checkOut, rt.id, blockedDates);
  if (blocked.length > 0) return false;

  // Vérifier si au moins une chambre physique est libre
  const roomsOfType = rooms.filter(r => rt.assignedRooms?.includes(r.id));
  return roomsOfType.some(room =>
    isRoomAvailableForPeriod(room.id, checkIn, checkOut, reservations, roomBlocks)
  );
}

function getAvailableRoomTypes(checkIn, checkOut, rooms, roomTypes, reservations, roomBlocks, blockedDates) {
  return roomTypes.filter(rt =>
    !rt.isInternal &&
    isRoomTypeAvailableForPeriod(rt, checkIn, checkOut, rooms, reservations, roomBlocks, blockedDates)
  );
}

function detectSplit(checkIn, checkOut, rooms, roomTypes, reservations, roomBlocks, blockedDates) {
  const cur = new Date(isoToDate(checkIn));
  cur.setDate(cur.getDate() + 1);
  const endMinus1 = new Date(isoToDate(checkOut));
  endMinus1.setDate(endMinus1.getDate() - 1);

  while (cur <= endMinus1) {
    const mid = cur.toISOString().split('T')[0];
    const seg1 = getAvailableRoomTypes(checkIn, mid, rooms, roomTypes, reservations, roomBlocks, blockedDates);
    const seg2 = getAvailableRoomTypes(mid, checkOut, rooms, roomTypes, reservations, roomBlocks, blockedDates);
    if (seg1.length > 0 && seg2.length > 0) return { mid, seg1, seg2 };
    cur.setDate(cur.getDate() + 1);
  }
  return null;
}

function priceLabel(rt) {
  return rt.basePrice ? `à partir de ${rt.basePrice}€/nuit` : 'prix sur demande';
}

// Analyse nuit par nuit : trouve les nuits bloquées et les sous-fenêtres disponibles
function analyzePartialAvailability(checkIn, checkOut, rooms, roomTypes, reservations, roomBlocks, blockedDates) {
  const nights = dateRangeNights(checkIn, checkOut);
  const publicTypes = roomTypes.filter(rt => !rt.isInternal);

  // Disponibilité par nuit
  const nightAvail = nights.map(night => {
    const nextDay = new Date(night + 'T12:00:00');
    nextDay.setDate(nextDay.getDate() + 1);
    const nextDayStr = nextDay.toISOString().split('T')[0];
    const freeTypes = publicTypes.filter(rt =>
      isRoomTypeAvailableForPeriod(rt, night, nextDayStr, rooms, reservations, roomBlocks, blockedDates)
    );
    return { date: night, freeTypes };
  });

  const blockedNights = nightAvail.filter(n => n.freeTypes.length === 0).map(n => n.date);

  // Sous-fenêtres consécutives où au moins 1 chambre est libre chaque nuit
  const subWindows = [];
  let wStart = null;
  for (let i = 0; i <= nightAvail.length; i++) {
    const free = i < nightAvail.length && nightAvail[i].freeTypes.length > 0;
    if (free && wStart === null) {
      wStart = i;
    } else if (!free && wStart !== null) {
      const wFrom = nights[wStart];
      const wTo = i < nightAvail.length ? nights[i] : checkOut;
      const wNights = i - wStart;
      // Chercher quelles chambres sont dispo sur TOUTE cette sous-fenêtre
      const availForWindow = getAvailableRoomTypes(wFrom, wTo, rooms, roomTypes, reservations, roomBlocks, blockedDates);
      subWindows.push({ from: wFrom, to: wTo, nights: wNights, rooms: availForWindow });
      wStart = null;
    }
  }

  return { blockedNights, subWindows };
}

// ─────────────────────────────────────────────────────────────────────────────
// DÉTECTION DE DATES EN FRANÇAIS
// ─────────────────────────────────────────────────────────────────────────────

const MOIS = {
  janvier:1, février:2, fevrier:2, mars:3, avril:4, mai:5, juin:6,
  juillet:7, août:8, aout:8, septembre:9, octobre:10, novembre:11,
  décembre:12, decembre:12,
};

function parseFrenchDates(text) {
  const t = text.toLowerCase();
  const year = new Date().getFullYear();

  const p1 = /du\s+(\d{1,2})(?:er)?\s+(janvier|f[eé]vrier|mars|avril|mai|juin|juillet|ao[uû]t|septembre|octobre|novembre|d[eé]cembre)\s+au\s+(\d{1,2})(?:er)?\s+(janvier|f[eé]vrier|mars|avril|mai|juin|juillet|ao[uû]t|septembre|octobre|novembre|d[eé]cembre)/i;
  const p2 = /du\s+(\d{1,2})(?:er)?\s+au\s+(\d{1,2})(?:er)?\s+(janvier|f[eé]vrier|mars|avril|mai|juin|juillet|ao[uû]t|septembre|octobre|novembre|d[eé]cembre)/i;

  let m = t.match(p1);
  if (m) {
    const m2 = MOIS[m[2]], m4 = MOIS[m[4]];
    return {
      checkIn: `${year}-${String(m2).padStart(2,'0')}-${String(m[1]).padStart(2,'0')}`,
      checkOut: `${(m4 < m2 ? year+1 : year)}-${String(m4).padStart(2,'0')}-${String(m[3]).padStart(2,'0')}`,
    };
  }
  m = t.match(p2);
  if (m) {
    const mo = String(MOIS[m[3]]).padStart(2,'0');
    return {
      checkIn:  `${year}-${mo}-${String(m[1]).padStart(2,'0')}`,
      checkOut: `${year}-${mo}-${String(m[2]).padStart(2,'0')}`,
    };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXTE DYNAMIQUE (injecté dans le message utilisateur)
// ─────────────────────────────────────────────────────────────────────────────

function buildDynamicContext(checkIn, checkOut, firestoreData, reservations) {
  const { rooms, roomTypes, availabilityRules, blockedDates, defaultRules, roomBlocks } = firestoreData;
  const nights = nightsBetween(checkIn, checkOut);
  const lines = [`[DONNÉES TEMPS RÉEL — Firestore — ${checkIn} → ${checkOut} (${nights} nuit${nights > 1 ? 's' : ''}) :]`];

  // 1. Vérification séjour minimum
  const publicRoomTypes = roomTypes.filter(rt => !rt.isInternal);
  const minStayViolations = [];
  publicRoomTypes.forEach(rt => {
    const rule = getApplicableRule(checkIn, checkOut, rt.id, availabilityRules);
    const minNights = rule?.minNights ?? defaultRules.minNights;
    if (nights < minNights) {
      minStayViolations.push(`${rt.name} : minimum ${minNights} nuits${rule ? ` (règle "${rule.name}")` : ' (règle générale)'}`);
    }
  });
  if (minStayViolations.length > 0) {
    lines.push(`⚠️ DURÉE INSUFFISANTE (${nights} nuit${nights>1?'s':''} demandée${nights>1?'s':''}) :`);
    minStayViolations.forEach(v => lines.push(`  - ${v}`));
  }

  // 2. Vérification dates bloquées globalement dans la période
  const allBlockedInPeriod = new Set();
  publicRoomTypes.forEach(rt => {
    getBlockedNightsInPeriod(checkIn, checkOut, rt.id, blockedDates).forEach(bd => {
      const bdStr = bd.date instanceof Date ? bd.date.toISOString().split('T')[0] : bd.date?.toDate?.()?.toISOString().split('T')[0];
      if (bdStr) allBlockedInPeriod.add(`${bdStr}${bd.reason ? ` (${bd.reason})` : ''}`);
    });
  });
  if (allBlockedInPeriod.size > 0) {
    lines.push(`⛔ NUITS BLOQUÉES DANS LA PÉRIODE :`);
    [...allBlockedInPeriod].forEach(d => lines.push(`  - ${d}`));
  }

  // 3. Disponibilité réelle des chambres
  const available = getAvailableRoomTypes(checkIn, checkOut, rooms, roomTypes, reservations, roomBlocks, blockedDates);

  if (available.length > 0) {
    lines.push('✅ CHAMBRES DISPONIBLES SUR TOUTE LA PÉRIODE :');
    available.forEach(rt => {
      const rule = getApplicableRule(checkIn, checkOut, rt.id, availabilityRules);
      const minNights = rule?.minNights ?? defaultRules.minNights;
      const totalPrice = rt.basePrice ? `${rt.basePrice * nights}€ total` : 'prix sur demande';
      const minOk = nights >= minNights ? '' : ` ⚠️ min ${minNights} nuits`;
      lines.push(`  ✓ ${rt.name} (max ${rt.maxOccupancy || rt.maxGuests || '?'} pers.) — ${priceLabel(rt)} — ${totalPrice}${minOk}`);
    });
    const unavailable = publicRoomTypes.filter(rt => !available.find(a => a.id === rt.id));
    if (unavailable.length > 0) {
      lines.push('❌ INDISPONIBLES :');
      unavailable.forEach(rt => lines.push(`  ✗ ${rt.name}`));
    }
  } else {
    lines.push('❌ AUCUNE CHAMBRE DISPONIBLE SUR TOUTE LA PÉRIODE.');

    // Analyse nuit par nuit pour identifier exactement le problème et les alternatives
    const analysis = analyzePartialAvailability(checkIn, checkOut, rooms, roomTypes, reservations, roomBlocks, blockedDates);

    if (analysis.blockedNights.length > 0) {
      lines.push(`  Nuits bloquées (aucune chambre libre) : ${analysis.blockedNights.join(', ')}`);
    }

    if (analysis.subWindows.length > 0) {
      lines.push('  SOUS-PÉRIODES DISPONIBLES DANS VOS DATES (alternatives possibles) :');
      analysis.subWindows.forEach(w => {
        const rule = getApplicableRule(w.from, w.to, w.rooms[0]?.id, availabilityRules);
        const minN = rule?.minNights ?? defaultRules.minNights;
        const minOk = w.nights >= minN;
        const roomNames = w.rooms.length > 0 ? w.rooms.map(r => r.name).join(' ou ') : 'aucune chambre unique sur cette fenêtre';
        const totalP = w.rooms[0]?.basePrice ? `${w.rooms[0].basePrice * w.nights}€` : 'prix sur demande';
        lines.push(`    → Du ${w.from} au ${w.to} (${w.nights} nuit${w.nights>1?'s':''}) : ${roomNames} — ${totalP} ${minOk ? '✓ durée ok' : `⚠️ en dessous du minimum (${minN} nuits requises)`}`);
      });
    } else {
      // Essayer split stay (changement de chambre)
      const split = detectSplit(checkIn, checkOut, rooms, roomTypes, reservations, roomBlocks, blockedDates);
      if (split) {
        const n1 = nightsBetween(checkIn, split.mid);
        const n2 = nightsBetween(split.mid, checkOut);
        lines.push(`🔀 SPLIT STAY POSSIBLE — changement de chambre le ${split.mid} :`);
        lines.push(`  Segment 1 (${checkIn} → ${split.mid}, ${n1} nuit${n1>1?'s':''}) :`);
        split.seg1.forEach(rt => lines.push(`    ✓ ${rt.name} — ${rt.basePrice ? rt.basePrice * n1 + '€' : 'prix sur demande'}`));
        lines.push(`  Segment 2 (${split.mid} → ${checkOut}, ${n2} nuit${n2>1?'s':''}) :`);
        split.seg2.forEach(rt => lines.push(`    ✓ ${rt.name} — ${rt.basePrice ? rt.basePrice * n2 + '€' : 'prix sur demande'}`));
      } else {
        lines.push('  Aucune sous-période ni split stay possible sur ces dates.');
      }
    }
  }

  lines.push('[FIN DONNÉES TEMPS RÉEL]');
  return lines.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// SYSTEM PROMPT — construit depuis les données Firestore réelles
// ─────────────────────────────────────────────────────────────────────────────

function buildSystemPrompt(firestoreData) {
  const { roomTypes, availabilityRules, blockedDates, defaultRules } = firestoreData;
  const publicRooms = roomTypes.filter(rt => !rt.isInternal);

  // Liste des chambres
  const roomList = publicRooms.map(rt =>
    `- ${rt.name} (max ${rt.maxOccupancy || rt.maxGuests || '?'} pers.) — ${priceLabel(rt)}`
  ).join('\n');

  // Règles de séjour minimum
  const rulesText = availabilityRules.length > 0
    ? availabilityRules.map(rule => {
        const types = rule.roomTypes.length > 0
          ? publicRooms.filter(rt => rule.roomTypes.includes(rt.id)).map(rt => rt.name).join(', ')
          : 'toutes les chambres';
        const msg = rule.customMessage ? ` — Note : "${rule.customMessage}"` : '';
        return `- "${rule.name}" du ${toDateStr(rule.startDate)} au ${toDateStr(rule.endDate)} : minimum ${rule.minNights} nuits (${types})${msg}`;
      }).join('\n')
    : '- Aucune règle spécifique enregistrée';

  // Dates bloquées (résumé)
  const blockedText = blockedDates.length > 0
    ? blockedDates.map(bd => {
        const types = bd.roomTypes.length > 0
          ? publicRooms.filter(rt => bd.roomTypes.includes(rt.id)).map(rt => rt.name).join(', ')
          : 'toutes chambres';
        return `- ${toDateStr(bd.date)} : fermé (${types})${bd.reason ? ` — ${bd.reason}` : ''}`;
      }).join('\n')
    : '- Aucune date bloquée enregistrée';

  return `Tu es le concierge virtuel de la ${HOTEL.name}, une maison d'hôtes de charme en ${HOTEL.location}.
Tu réponds aux questions des clients sur les disponibilités, le restaurant et les services.

## Règles de comportement
- Réponds TOUJOURS en français, ton chaleureux et professionnel.
- Pour les disponibilités et les contraintes : base-toi EXCLUSIVEMENT sur les blocs [DONNÉES TEMPS RÉEL] injectés dans les messages. Ne jamais inventer.
- Si aucun bloc n'est présent dans le message, demande les dates au client avant de répondre.
- Si une information manque, propose de contacter l'hôtel : ${HOTEL.contact}.
- Arrivée à partir de ${HOTEL.checkIn}, départ avant ${HOTEL.checkOut}.

## Chambres disponibles à la réservation
${roomList}

## Règles de séjour minimum (données Firestore)
- Séjour minimum par défaut : ${defaultRules.minNights} nuits / maximum : ${defaultRules.maxNights} nuits
${rulesText}

## Dates fermées (données Firestore)
${blockedText}

## Restaurant
- Services : ${RESTAURANT.service}
- Fermé : ${RESTAURANT.closed}
- Menu : ${RESTAURANT.menu}
- Réservation : ${RESTAURANT.reservationNote}

## Services & extras
${SERVICES.map(s => `- ${s}`).join('\n')}

## Guidance split stay
Quand le bloc indique "SPLIT STAY POSSIBLE" :
1. Présente positivement l'option séjour avec changement de chambre.
2. Détaille les 2 segments (chambre + dates + prix) depuis le bloc.
3. Précise que 2 réservations séparées sont nécessaires sur le site.
4. Rassure : transfert des bagages, petits-déjeuners et espaces communs continus.

Quand le bloc indique une durée insuffisante (⚠️) :
- Explique gentiment la contrainte de séjour minimum pour la période concernée.
- Propose les dates alternatives les plus proches qui respectent le minimum.`;
}

// ─────────────────────────────────────────────────────────────────────────────
// APPEL API CLAUDE
// ─────────────────────────────────────────────────────────────────────────────

async function callClaude(history, systemPrompt) {
  const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
  if (!apiKey) throw new Error('Clé API manquante — ajouter REACT_APP_ANTHROPIC_API_KEY dans .env.local');

  const res = await fetch('/api/anthropic/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-haiku-4-5-20251001',
      max_tokens: 1200,
      system: systemPrompt,
      messages: history,
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Erreur HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.content[0].text;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANT
// ─────────────────────────────────────────────────────────────────────────────

const WELCOME = `Bonjour ! Je suis le concierge virtuel de la Maison Ceronne. 😊

Je peux vous aider à :
• Vérifier les disponibilités pour vos dates
• Vous informer sur les contraintes de séjour et dates fermées
• Vous guider si un changement de chambre est nécessaire
• Répondre à vos questions sur le restaurant et nos services

Comment puis-je vous aider ?`;

const INITIAL_FS_DATA = {
  roomTypes: [], rooms: [], availabilityRules: [], blockedDates: [],
  defaultRules: { minNights: 2, maxNights: 30 }, roomBlocks: [],
  loaded: false, error: null,
};

export default function ChatTest() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([{ role: 'assistant', content: WELCOME }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fsData, setFsData] = useState(INITIAL_FS_DATA);
  const [reservations, setReservations] = useState([]);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    async function loadFirestore() {
      try {
        // Chargement en parallèle : données initiales + blocages physiques + réservations à venir
        const [initialData, roomBlocksSnap, resSnap] = await Promise.all([
          loadInitialData(false),
          getDocs(collection(db, 'room_blocks')),
          getDocs(query(
            collection(db, 'reservations'),
            where('checkOut', '>=', Timestamp.fromDate(Object.assign(new Date(), { hours: 0, minutes: 0, seconds: 0, ms: 0 })))
          )),
        ]);

        const roomBlocks = roomBlocksSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const res = resSnap.docs
          .map(d => {
            const r = d.data();
            return {
              id: d.id, ...r,
              _checkInNorm: r.checkIn?.toDate ? r.checkIn.toDate() : new Date(r.checkIn),
              _checkOutNorm: r.checkOut?.toDate ? r.checkOut.toDate() : new Date(r.checkOut),
            };
          })
          .filter(r => r.status !== 'cancelled');

        setFsData({ ...initialData, roomBlocks, loaded: true, error: null });
        setReservations(res);
      } catch (e) {
        console.error('Erreur chargement Firestore:', e);
        setFsData(prev => ({ ...prev, loaded: true, error: e.message }));
      }
    }
    loadFirestore();
  }, []);

  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, messages]);

  const reset = () => {
    setMessages([{ role: 'assistant', content: WELCOME }]);
    setError(null);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      let augmentedText = text;
      if (fsData.loaded && !fsData.error) {
        const dates = parseFrenchDates(text);
        if (dates) {
          augmentedText = `${text}\n\n${buildDynamicContext(dates.checkIn, dates.checkOut, fsData, reservations)}`;
        }
      }

      const systemPrompt = buildSystemPrompt(fsData);

      const apiHistory = [...messages, { role: 'user', content: text }]
        .filter((m, i) => !(i === 0 && m.role === 'assistant'))
        .map((m, i, arr) =>
          i === arr.length - 1 && m.role === 'user'
            ? { ...m, content: augmentedText }
            : m
        );

      const reply = await callClaude(apiHistory, systemPrompt);
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const statusColor = !fsData.loaded ? 'bg-amber-50 text-amber-700'
    : fsData.error ? 'bg-red-50 text-red-600'
    : 'bg-green-50 text-green-700';

  const statusText = !fsData.loaded ? '⏳ Chargement Firestore en cours…'
    : fsData.error ? `⚠️ Erreur : ${fsData.error}`
    : `✅ ${fsData.roomTypes.filter(rt=>!rt.isInternal).length} types de chambres · ${reservations.length} réservations · ${fsData.availabilityRules.length} règles · ${fsData.blockedDates.length} dates bloquées`;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Bot className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-1">Prototype Chat Concierge</h1>
          <p className="text-gray-400 text-xs mb-4">Disponibilités · Règles · Dates fermées — lecture seule Firestore</p>

          <div className={`text-xs px-3 py-2 rounded-lg mb-6 text-left ${statusColor}`}>
            {statusText}
          </div>

          <div className="text-left space-y-1 text-sm text-gray-600 bg-gray-50 rounded-xl p-4 mb-6">
            <p className="font-medium text-gray-700 mb-2">Questions à tester :</p>
            {[
              'Avez-vous une chambre disponible du 1er au 5 juillet ?',
              'Je voudrais rester 1 nuit le 15 juillet, c\'est possible ?',
              'Avez-vous quelque chose du 10 au 20 juillet ?',
              'Est-ce qu\'on peut dîner au restaurant ce soir ?',
              'Quels services proposez-vous ?',
            ].map((q, i) => (
              <button key={i} onClick={() => { setOpen(true); setInput(q); }}
                className="block w-full text-left px-3 py-2 rounded-lg hover:bg-amber-50 hover:text-amber-700 transition-colors border border-transparent hover:border-amber-200">
                <span className="text-amber-500 mr-2">→</span>{q}
              </button>
            ))}
          </div>

          <button onClick={() => setOpen(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white font-medium px-6 py-3 rounded-xl transition-colors">
            Ouvrir le chat
          </button>
        </div>

        <p className="text-xs text-gray-400">
          Modèle : claude-haiku-4-5 · Données : Firestore réel · Test local uniquement
        </p>
      </div>

      {!open && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-amber-600 hover:bg-amber-700 text-white rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-110 z-50">
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden"
          style={{ height: '540px' }}>

          <div className="bg-amber-600 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Concierge Maison Ceronne</p>
                <p className="text-amber-100 text-xs">
                  {!fsData.loaded ? 'Chargement…' : fsData.error ? '⚠️ Données partielles' : 'Données en temps réel'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={reset} title="Nouvelle conversation"
                className="w-8 h-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg flex items-center justify-center transition-colors">
                <RefreshCw className="w-4 h-4" />
              </button>
              <button onClick={() => setOpen(false)}
                className="w-8 h-8 text-white/70 hover:text-white hover:bg-white/10 rounded-lg flex items-center justify-center transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[82%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'bg-amber-600 text-white rounded-br-sm'
                    : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                  <span className="text-gray-400 text-sm">En train de répondre…</span>
                </div>
              </div>
            )}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-600">
                {error}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-gray-100 p-3 flex gap-2">
            <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey} placeholder="Votre question…" rows={1}
              className="flex-1 resize-none border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-amber-400 transition-colors"
              style={{ maxHeight: '80px' }} />
            <button onClick={send} disabled={loading || !input.trim() || !fsData.loaded}
              className="w-9 h-9 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-200 text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0 self-end">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
